// ===========================================
// Xero React Query Hooks
// ===========================================
// Custom hooks for Xero API operations
// OAuth, sync control, account mappings

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  xeroApi,
  UpdateSettingsInput,
  AccountMappingInput,
  TriggerSyncInput,
} from '../api/xero.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const xeroKeys = {
  all: ['xero'] as const,
  status: () => [...xeroKeys.all, 'status'] as const,
  accounts: () => [...xeroKeys.all, 'accounts'] as const,
  bankAccounts: () => [...xeroKeys.all, 'bankAccounts'] as const,
  syncStats: () => [...xeroKeys.all, 'syncStats'] as const,
  syncErrors: () => [...xeroKeys.all, 'syncErrors'] as const,
};

// ===========================================
// AUTH QUERIES
// ===========================================

/**
 * Check if Xero is connected for the school
 */
export function useXeroStatus() {
  return useQuery({
    queryKey: xeroKeys.status(),
    queryFn: () => xeroApi.getStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get OAuth authorization URL
 */
export function useXeroAuthUrl() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => xeroApi.getAuthUrl(),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to get authorization URL', {
        variant: 'error',
      });
    },
  });
}

/**
 * Disconnect Xero integration
 */
export function useDisconnectXero() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => xeroApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: xeroKeys.all });
      enqueueSnackbar('Xero disconnected successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to disconnect Xero', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// SETTINGS MUTATIONS
// ===========================================

/**
 * Update Xero integration settings
 */
export function useUpdateXeroSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: UpdateSettingsInput) => xeroApi.updateSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: xeroKeys.status() });
      enqueueSnackbar('Settings updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update settings', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update account mappings
 */
export function useUpdateAccountMappings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: AccountMappingInput) => xeroApi.updateAccountMappings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: xeroKeys.status() });
      enqueueSnackbar('Account mappings updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update account mappings', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// ACCOUNT QUERIES
// ===========================================

/**
 * List available Xero accounts
 */
export function useXeroAccounts() {
  return useQuery({
    queryKey: xeroKeys.accounts(),
    queryFn: () => xeroApi.listAccounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Only fetch when explicitly called
  });
}

/**
 * List available Xero bank accounts
 */
export function useXeroBankAccounts() {
  return useQuery({
    queryKey: xeroKeys.bankAccounts(),
    queryFn: () => xeroApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Only fetch when explicitly called
  });
}

// ===========================================
// SYNC QUERIES & MUTATIONS
// ===========================================

/**
 * Get sync statistics
 */
export function useXeroSyncStats() {
  return useQuery({
    queryKey: xeroKeys.syncStats(),
    queryFn: () => xeroApi.getSyncStats(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Get entities with sync errors
 */
export function useXeroSyncErrors() {
  return useQuery({
    queryKey: xeroKeys.syncErrors(),
    queryFn: () => xeroApi.getSyncErrors(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Trigger manual sync
 */
export function useTriggerXeroSync() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input?: TriggerSyncInput) => xeroApi.triggerSync(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: xeroKeys.status() });
      queryClient.invalidateQueries({ queryKey: xeroKeys.syncStats() });
      queryClient.invalidateQueries({ queryKey: xeroKeys.syncErrors() });

      const contactsSynced = data.contacts?.synced || 0;
      const invoicesSynced = data.invoices?.synced || 0;
      const contactErrors = data.contacts?.errors || 0;
      const invoiceErrors = data.invoices?.errors || 0;
      const totalErrors = contactErrors + invoiceErrors;

      if (totalErrors > 0) {
        enqueueSnackbar(
          `Sync completed with errors. Synced: ${contactsSynced} contacts, ${invoicesSynced} invoices. Errors: ${totalErrors}`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(
          `Sync completed. Synced: ${contactsSynced} contacts, ${invoicesSynced} invoices`,
          { variant: 'success' }
        );
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to trigger sync', {
        variant: 'error',
      });
    },
  });
}

/**
 * Retry sync for a specific entity
 */
export function useRetryXeroSync() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: 'invoice' | 'payment' | 'contact'; entityId: string }) =>
      xeroApi.retrySync(entityType, entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: xeroKeys.syncStats() });
      queryClient.invalidateQueries({ queryKey: xeroKeys.syncErrors() });
      enqueueSnackbar('Retry queued successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to queue retry', {
        variant: 'error',
      });
    },
  });
}
